import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { TabPanel } from 'primereact/tabview'

import {
  Btn,
  CustomCheckbox,
  CustomDataTableHeader,
  CustomDropdown,
  CustomModal,
  CustomMultiSelect,
  CustomRadioButton,
  CustomTabView,
  Loading,
  Periodo,
} from '../../../../components'
import { tableCellCentered } from '../../../../assets/js/styles'
import { showMessageBox } from '../../../../store/actions/system.actions'
import { parseToMysqlFormat } from '../../../../services/util/date.service'
import PainelMonitoramentoEntregasHttp from '../../../../services/api/https/dashboard/painel-monitoramento-entregas.http'

const ROTAS_OPTIONS = [
  { value: 'em_andamento', label: 'Apenas Rotas em Andamento no período' },
  { value: 'todas', label: 'Todas as Rotas no período' },
]

const STATUS_OPTIONS = [
  { label: 'Em Rota', value: 'Em Rota' },
  { label: 'Parado', value: 'Parado' },
]

function getPeriodoHoje() {
  const hoje = new Date()
  return {
    dataInicial: new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 0, 0, 0),
    dataFinal: new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 23, 59, 59),
  }
}

function ModalDelegarRotas({ visible, onClose, analistaOrigem, onSuccess, dispatch }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [maximized, setMaximized] = useState(false)

  useEffect(() => {
    if (visible) {
      setActiveIndex(0)
      setMaximized(false)
    }
  }, [visible])

  return (
    <CustomModal
      header="Delegar Rotas/Veículos"
      isVisible={visible}
      onClose={onClose}
      maximizable
      maximized={maximized}
      onMaximize={e => setMaximized(e.maximized)}
      width={maximized ? '100vw' : 'min(94vw, 1200px)'}
      style={maximized ? { height: '100vh' } : undefined}
      contentStyle={{ overflowY: 'auto', maxHeight: maximized ? 'calc(100vh - 110px)' : '80vh' }}
    >
      <p style={{ marginBottom: 12 }}>
        <strong>Analista de Origem:</strong> {analistaOrigem ? analistaOrigem.analista_name : '-'}
      </p>

      {visible && analistaOrigem && (
        <CustomTabView activeIndex={activeIndex} onTabChange={setActiveIndex}>
          <TabPanel header="Rotas">
            <DelegarRotasTab
              analistaOrigem={analistaOrigem}
              dispatch={dispatch}
              onSuccess={onSuccess}
            />
          </TabPanel>
          <TabPanel header="Veículos">
            <DelegarVeiculosTab
              analistaOrigem={analistaOrigem}
              dispatch={dispatch}
              onSuccess={onSuccess}
            />
          </TabPanel>
        </CustomTabView>
      )}
    </CustomModal>
  )
}

function DelegarRotasTab({ analistaOrigem, dispatch, onSuccess }) {
  const periodoInicial = useMemo(() => getPeriodoHoje(), [])
  const [pInicial, setPInicial] = useState(periodoInicial.dataInicial)
  const [pFinal, setPFinal] = useState(periodoInicial.dataFinal)
  const [rotasOption, setRotasOption] = useState('em_andamento')
  const [rotas, setRotas] = useState([])
  const [analistas, setAnalistas] = useState([])
  const [destino, setDestino] = useState(null)
  const [tpFilter, setTpFilter] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchRotas()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRotas = async () => {
    if (!pInicial || !pFinal) {
      dispatch(showMessageBox(null, 'Informe o Período Inicial e o Período Final'))
      return
    }

    try {
      setLoading(true)
      const data = await PainelMonitoramentoEntregasHttp.getDelegacaoRotas(
        analistaOrigem.analista_entrega_id,
        rotasOption,
        parseToMysqlFormat(pInicial),
        parseToMysqlFormat(pFinal),
      )

      setRotas(
        (data.rotas || []).map(rota => ({
          ...rota,
          selected: false,
          delegar_analista_id: null,
          delegar_analista_name: '',
        })),
      )
      setAnalistas(data.analistas || [])
      setTpFilter([])
    } catch (error) {
      setRotas([])
      dispatch(showMessageBox(null, error))
    } finally {
      setLoading(false)
    }
  }

  const analistasDestino = useMemo(
    () =>
      analistas.filter(
        analista => analista.analista_entrega_id !== analistaOrigem.analista_entrega_id,
      ),
    [analistas, analistaOrigem],
  )

  const tpOptions = useMemo(() => {
    const byId = new Map()
    rotas.forEach(rota => byId.set(rota.tp_id, { tp_id: rota.tp_id, tp_name: rota.tp_name }))
    return Array.from(byId.values()).sort((a, b) => a.tp_name.localeCompare(b.tp_name))
  }, [rotas])

  const rotasVisiveis = useMemo(() => {
    if (!tpFilter.length) return rotas
    const tpIds = new Set(tpFilter.map(tp => tp.tp_id))
    return rotas.filter(rota => tpIds.has(rota.tp_id))
  }, [rotas, tpFilter])

  const isRowBlocked = rota =>
    !!destino && (!destino.tp_ids || !destino.tp_ids.includes(rota.tp_id))

  const rotasSelecionaveis = rotasVisiveis.filter(rota => !isRowBlocked(rota))
  const todasSelecionadas =
    rotasSelecionaveis.length > 0 && rotasSelecionaveis.every(rota => rota.selected)
  const temDelegacao = rotas.some(rota => rota.delegar_analista_id)

  const toggleSelecionarTodos = checked => {
    const visiveisIds = new Set(rotasSelecionaveis.map(rota => rota.fleet_id))
    setRotas(prev =>
      prev.map(rota => (visiveisIds.has(rota.fleet_id) ? { ...rota, selected: checked } : rota)),
    )
  }

  const toggleSelecionado = (rota, checked) => {
    setRotas(prev =>
      prev.map(item => (item.fleet_id === rota.fleet_id ? { ...item, selected: checked } : item)),
    )
  }

  const associar = () => {
    if (!destino) {
      dispatch(showMessageBox(null, 'Selecione o Analista de destino antes de delegar.'))
      return
    }

    const selecionadas = rotas.filter(rota => rota.selected && !isRowBlocked(rota))
    if (selecionadas.length === 0) {
      dispatch(showMessageBox(null, 'Selecione pelo menos uma rota na tabela.'))
      return
    }

    setRotas(prev =>
      prev.map(rota =>
        rota.selected && !isRowBlocked(rota)
          ? {
              ...rota,
              delegar_analista_id: destino.analista_entrega_id,
              delegar_analista_name: destino.analista_name,
              selected: false,
            }
          : rota,
      ),
    )
  }

  const salvar = async () => {
    const delegacoes = rotas
      .filter(rota => rota.delegar_analista_id)
      .map(rota => ({
        fleet_id: rota.fleet_id,
        analista_destino_id: rota.delegar_analista_id,
      }))

    if (delegacoes.length === 0) {
      dispatch(showMessageBox(null, 'Faça pelo menos uma associação antes de Salvar'))
      return
    }

    try {
      setSaving(true)
      const result = await PainelMonitoramentoEntregasHttp.salvarDelegacaoRotas(
        analistaOrigem.analista_entrega_id,
        delegacoes,
      )
      dispatch(showMessageBox(`${result.qte_rotas_delegadas} rota(s) delegada(s) com sucesso!`))
      await fetchRotas()
      if (onSuccess) onSuccess()
    } catch (error) {
      dispatch(showMessageBox(null, error))
    } finally {
      setSaving(false)
    }
  }

  const rowClassName = rota => (isRowBlocked(rota) ? { 'p-disabled': true } : {})

  return (
    <div style={{ padding: '12px 4px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.8rem', color: '#14b0bf', marginBottom: 2 }}>
            Data Prevista da Rota
          </div>
          <Periodo
            periodoInicial={pInicial}
            periodoFinal={pFinal}
            showButton={false}
            onChangePeriodoInicial={setPInicial}
            onChangePeriodoFinal={setPFinal}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 4 }}>
          {ROTAS_OPTIONS.map(option => (
            <CustomRadioButton
              key={option.value}
              items={[option]}
              value={rotasOption}
              onChange={setRotasOption}
            />
          ))}
        </div>
        <div style={{ paddingBottom: 4 }}>
          <Btn onClick={fetchRotas} disabled={loading}>
            Buscar Rotas
          </Btn>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 280 }}>
          <CustomDropdown
            showLabel
            filter
            label="Delegar Para"
            optionLabel="analista_name"
            options={analistasDestino}
            value={destino}
            onChange={setDestino}
            placeholder="Selecione o Analista..."
            appendTo={document.body}
          />
        </div>
        <div style={{ minWidth: 240 }}>
          <CustomMultiSelect
            label="Filtrar por TP"
            optionLabel="tp_name"
            options={tpOptions}
            value={tpFilter}
            onChange={setTpFilter}
            appendTo={document.body}
          />
        </div>
      </div>

      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 10px 0' }}
      >
        <CustomCheckbox checked={todasSelecionadas} onChange={toggleSelecionarTodos}>
          Selecionar Todos
        </CustomCheckbox>
        <Btn onClick={associar} disabled={loading || saving}>
          Delegar
        </Btn>
        <Btn onClick={salvar} disabled={loading || !temDelegacao} isLoading={saving}>
          Salvar
        </Btn>
      </div>

      {loading ? (
        <Loading size="md" />
      ) : (
        <DataTable
          header={<CustomDataTableHeader onChange={setGlobalFilter} />}
          globalFilter={globalFilter}
          value={rotasVisiveis}
          paginator
          rows={10}
          rowClassName={rowClassName}
          emptyMessage="Nenhuma rota encontrada para o Analista no período selecionado"
        >
          <Column
            header=""
            style={tableCellCentered(50)}
            body={rota => (
              <CustomCheckbox
                checked={rota.selected === true}
                disabled={isRowBlocked(rota) || saving}
                onChange={checked => toggleSelecionado(rota, checked)}
              />
            )}
          />
          <Column header="Veículo" field="vehicle_badge" style={tableCellCentered()} />
          <Column header="Motorista" field="driver_name" style={tableCellCentered()} />
          <Column header="Data Prevista" field="expected_date" style={tableCellCentered()} />
          <Column header="TP" field="tp_name" style={tableCellCentered()} />
          <Column header="Delegar Para" field="delegar_analista_name" style={tableCellCentered()} />
        </DataTable>
      )}
    </div>
  )
}

function DelegarVeiculosTab({ analistaOrigem, dispatch, onSuccess }) {
  const [tps, setTps] = useState([])
  const [selectedTp, setSelectedTp] = useState(null)
  const [buscou, setBuscou] = useState(false)
  const [veiculos, setVeiculos] = useState([])
  const [analistas, setAnalistas] = useState([])
  const [destino, setDestino] = useState(null)
  const [statusFilter, setStatusFilter] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const fetchTps = async () => {
      try {
        setLoading(true)
        const data = await PainelMonitoramentoEntregasHttp.getDelegacaoTps(
          analistaOrigem.analista_entrega_id,
        )
        setTps(data.tps || [])
      } catch (error) {
        dispatch(showMessageBox(null, error))
      } finally {
        setLoading(false)
      }
    }
    fetchTps()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVeiculos = async () => {
    if (!selectedTp) {
      dispatch(showMessageBox(null, 'Selecione um TP antes de buscar.'))
      return
    }

    try {
      setLoading(true)
      const data = await PainelMonitoramentoEntregasHttp.getDelegacaoVeiculos(
        analistaOrigem.analista_entrega_id,
        selectedTp.tp_id,
      )
      setVeiculos(
        (data.veiculos || []).map(veiculo => ({
          ...veiculo,
          selected: false,
          delegar_analista_id: null,
          delegar_analista_name: '',
        })),
      )
      setAnalistas(data.analistas || [])
      setDestino(null)
      setStatusFilter([])
      setBuscou(true)
    } catch (error) {
      setVeiculos([])
      dispatch(showMessageBox(null, error))
    } finally {
      setLoading(false)
    }
  }

  const analistasDestino = useMemo(
    () =>
      analistas.filter(
        analista => analista.analista_entrega_id !== analistaOrigem.analista_entrega_id,
      ),
    [analistas, analistaOrigem],
  )

  const veiculosVisiveis = useMemo(() => {
    if (!statusFilter.length) return veiculos
    return veiculos.filter(veiculo => statusFilter.includes(veiculo.status))
  }, [veiculos, statusFilter])

  const todasSelecionadas =
    veiculosVisiveis.length > 0 && veiculosVisiveis.every(veiculo => veiculo.selected)
  const temDelegacao = veiculos.some(veiculo => veiculo.delegar_analista_id)

  const toggleSelecionarTodos = checked => {
    const visiveisIds = new Set(veiculosVisiveis.map(veiculo => veiculo.vehicle_id))
    setVeiculos(prev =>
      prev.map(veiculo =>
        visiveisIds.has(veiculo.vehicle_id) ? { ...veiculo, selected: checked } : veiculo,
      ),
    )
  }

  const toggleSelecionado = (veiculo, checked) => {
    setVeiculos(prev =>
      prev.map(item =>
        item.vehicle_id === veiculo.vehicle_id ? { ...item, selected: checked } : item,
      ),
    )
  }

  const associar = () => {
    if (!destino) {
      dispatch(showMessageBox(null, 'Selecione o Analista de destino antes de delegar.'))
      return
    }

    const selecionados = veiculos.filter(veiculo => veiculo.selected)
    if (selecionados.length === 0) {
      dispatch(showMessageBox(null, 'Selecione pelo menos um veículo na tabela.'))
      return
    }

    setVeiculos(prev =>
      prev.map(veiculo =>
        veiculo.selected
          ? {
              ...veiculo,
              delegar_analista_id: destino.analista_entrega_id,
              delegar_analista_name: destino.analista_name,
              selected: false,
            }
          : veiculo,
      ),
    )
  }

  const salvar = async () => {
    const delegacoes = veiculos
      .filter(veiculo => veiculo.delegar_analista_id)
      .map(veiculo => ({
        vehicle_id: veiculo.vehicle_id,
        analista_destino_id: veiculo.delegar_analista_id,
      }))

    if (delegacoes.length === 0) {
      dispatch(showMessageBox(null, 'Faça pelo menos uma associação antes de Salvar'))
      return
    }

    try {
      setSaving(true)
      const result = await PainelMonitoramentoEntregasHttp.salvarDelegacaoVeiculos(
        analistaOrigem.analista_entrega_id,
        delegacoes,
      )
      dispatch(
        showMessageBox(`${result.qte_veiculos_delegados} veículo(s) delegado(s) com sucesso!`),
      )
      await fetchVeiculos()
      if (onSuccess) onSuccess()
    } catch (error) {
      dispatch(showMessageBox(null, error))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ padding: '12px 4px' }}>
      <p style={{ fontSize: '0.8rem', color: '#333333', marginBottom: 12 }}>
        Ao delegar um veículo, as próximas rotas iniciadas com ele serão vinculadas ao novo
        analista responsável
      </p>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 260 }}>
          <CustomDropdown
            showLabel
            filter
            label="TP"
            optionLabel="tp_name"
            options={tps}
            value={selectedTp}
            onChange={setSelectedTp}
            placeholder="Selecione o TP..."
            appendTo={document.body}
          />
        </div>
        <div style={{ paddingBottom: 2 }}>
          <Btn onClick={fetchVeiculos} disabled={loading}>
            Buscar
          </Btn>
        </div>
      </div>

      {buscou && (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 280 }}>
              <CustomDropdown
                showLabel
                filter
                label="Delegar Para"
                optionLabel="analista_name"
                options={analistasDestino}
                value={destino}
                onChange={setDestino}
                placeholder="Selecione o Analista..."
                appendTo={document.body}
              />
            </div>
            <div style={{ minWidth: 220 }}>
              <CustomMultiSelect
                label="Filtrar por Status"
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={setStatusFilter}
                filter={false}
                appendTo={document.body}
              />
            </div>
          </div>

          <div
            style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 10px 0' }}
          >
            <CustomCheckbox checked={todasSelecionadas} onChange={toggleSelecionarTodos}>
              Selecionar Todos
            </CustomCheckbox>
            <Btn onClick={associar} disabled={loading || saving}>
              Delegar
            </Btn>
            <Btn onClick={salvar} disabled={loading || !temDelegacao} isLoading={saving}>
              Salvar
            </Btn>
          </div>
        </>
      )}

      {loading ? (
        <Loading size="md" />
      ) : (
        buscou && (
          <DataTable
            header={<CustomDataTableHeader onChange={setGlobalFilter} />}
            globalFilter={globalFilter}
            value={veiculosVisiveis}
            paginator
            rows={10}
            emptyMessage="Nenhum veículo do TP está associado ao Analista de origem"
          >
            <Column
              header=""
              style={tableCellCentered(50)}
              body={veiculo => (
                <CustomCheckbox
                  checked={veiculo.selected === true}
                  disabled={saving}
                  onChange={checked => toggleSelecionado(veiculo, checked)}
                />
              )}
            />
            <Column header="Placa" field="vehicle_badge" style={tableCellCentered()} />
            <Column header="Capacidade KG" field="capacity_kg" style={tableCellCentered()} />
            <Column header="Status" field="status" style={tableCellCentered()} />
            <Column
              header="Delegar Para"
              field="delegar_analista_name"
              style={tableCellCentered()}
            />
          </DataTable>
        )
      )}
    </div>
  )
}

export default connect()(ModalDelegarRotas)
